import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";
import { mountWidgets } from "../components/widgets/container";

/**
 * Compact inline: larger rounded-square avatar on the left, a single featured
 * widget in the middle-right blank space, meta (views / clock / location) in
 * the top-right corner. Name + description sit under the avatar; any
 * additional widgets stack beneath the action row.
 *
 * The first widget in `config.widgets` is pulled out as the "featured"
 * embed that fills the middle-right region next to the avatar. All
 * remaining widgets render normally below.
 */
export function renderCompactInline(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  // Up to 2 widgets get pulled into the featured middle-right slot next to
  // the avatar; they stack vertically if both are present. Remaining widgets
  // render in the usual slot below. We re-mount each group so polling /
  // teardown lifecycles stay isolated.
  const featuredCfgs = config.widgets.slice(0, 2);
  const restCfg = config.widgets.slice(2);

  const overrideConfig: ProfileConfig = {
    ...config,
    widgets: restCfg,
  };

  const p = renderProfileParts(overrideConfig, options);
  p.card.classList.add("card--compact");
  if (featuredCfgs.length === 2) p.card.classList.add("card--compact-pair");
  p.avatar.classList.add("avatar--square");

  const featuredWidget = featuredCfgs.length > 0
    ? mountWidgets(
        // Force each featured widget into its own row so they always stack,
        // never pair side-by-side inside the featured column.
        featuredCfgs.map((w, i) => ({ ...w, row: 1000 + i })) as typeof featuredCfgs,
        config.profile.inlineClock,
      )
    : null;

  const meta = el("div", { class: "card__meta card__meta--top-right" });
  const vc = p.meta.getInline("views");     if (vc) meta.append(vc);
  const loc = p.meta.getInline("location");  if (loc) meta.append(loc);
  const clk = p.meta.getInline("clock");     if (clk) meta.append(clk);

  const topBar = el("div", { class: "card__top-bar" }, [
    el("div", { class: "card__top-left" }, [p.avatar]),
    el(
      "div",
      { class: "card__featured" },
      featuredWidget ? [featuredWidget] : [],
    ),
    meta,
  ]);

  const identity = el("div", { class: "card__identity card__identity--compact" }, [
    el("div", { class: "card__name-row" }, [p.username, p.badges]),
    p.description,
  ]);

  const actionRow = el("div", { class: "card__action-row" });
  if (p.socials) actionRow.append(p.socials);
  const ct = p.meta.getInline("contact"); if (ct) actionRow.append(ct);

  p.card.append(topBar, identity, actionRow);
  if (p.widgets) p.card.append(wrapSlot("widgets", p.widgets));
  if (p.links) p.card.append(wrapSlot("links", p.links));
  if (p.audio) p.card.append(wrapSlot("audio", p.audio));
  p.meta.applyCornerZones();

  return p.card;
}

function wrapSlot(name: string, child: HTMLElement): HTMLElement {
  return el("div", { class: `card__slot card__slot--${name}`, "data-slot": name }, [child]);
}
